export const TELEMETRY_EVENTS = {
  APP_OPENED: "app_opened",
  SESSION_CREATED: "session_created",
  SESSION_CREATE_FAILED: "session_create_failed",
  SESSION_JOIN_REQUESTED: "session_join_requested",
  SESSION_SNAPSHOT_LOADED: "session_snapshot_loaded",
  SESSION_RESET_REQUESTED: "session_reset_requested",
  SESSION_IMPORTED: "session_imported",
  SESSION_IMPORT_FAILED: "session_import_failed",
  SESSION_EXPORTED: "session_exported",
  SESSION_UPDATED_LOCAL: "session_updated_local",
  MEASUREMENT_CREATED: "measurement_created",
  MEASUREMENT_UPDATED: "measurement_updated",
  MEASUREMENT_DELETED: "measurement_deleted",
  TARGET_SAVED: "target_saved",
  TARGET_UPDATED: "target_updated",
  TARGET_VISIBILITY_TOGGLED: "target_visibility_toggled",
  TARGET_DELETED: "target_deleted",
  WS_CONNECTED: "ws_connected",
  WS_DISCONNECTED: "ws_disconnected",
  WS_RECONNECT_SCHEDULED: "ws_reconnect_scheduled",
  CALLSIGN_CONFLICT: "callsign_conflict",
  MUTATION_CONFLICT: "mutation_conflict",
  SERVER_ERROR: "server_error",
  VIEW_MODE_CHANGED: "view_mode_changed",
  REMOTE_CURSORS_TOGGLED: "remote_cursors_toggled",
  SIDEBAR_TOGGLED: "sidebar_toggled",
  ZOOM_FIT_POINTS_CLICKED: "zoom_fit_points_clicked",
  ZOOM_FIT_USERS_CLICKED: "zoom_fit_users_clicked",
  GEOCODE_TOGGLED: "geocode_toggled",
  GEOLOCATION_REQUESTED: "geolocation_requested",
  GEOLOCATION_SUCCEEDED: "geolocation_succeeded",
  GEOLOCATION_FAILED: "geolocation_failed",
  SHARE_LINK_COPY_SUCCEEDED: "share_link_copy_succeeded",
  SHARE_LINK_COPY_FAILED: "share_link_copy_failed",
  GEOCODE_LOOKUP_RESULT: "geocode_lookup_result",
  PLUS_CODE_ASSIGNED: "plus_code_assigned",
  SERVICE_WORKER_REGISTERED: "service_worker_registered",
  SERVICE_WORKER_REGISTRATION_FAILED: "service_worker_registration_failed",
  RUNTIME_ONLINE: "runtime_online",
  RUNTIME_OFFLINE: "runtime_offline"
} as const;

export type TelemetryEventName =
  (typeof TELEMETRY_EVENTS)[keyof typeof TELEMETRY_EVENTS];

type TelemetryPrimitive = string | number | boolean;
type TelemetryValue = TelemetryPrimitive | null | undefined;

export type TelemetryParams = Record<string, TelemetryValue>;

type InitTelemetryOptions = {
  measurementId?: string;
};

type GtagFn = (
  command: "js" | "config" | "event",
  target: string | Date,
  params?: Record<string, unknown>
) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: GtagFn;
  }
}

const SCRIPT_ID = "azimetria-ga4-script";
const BLOCKED_PARAM_KEYS = new Set([
  "name",
  "callsign",
  "email",
  "phone",
  "lat",
  "lon",
  "latitude",
  "longitude",
  "place_name",
  "placeName",
  "share_link"
]);

let activeMeasurementId: string | null = null;
let initialized = false;
let enabled = false;

const asSnakeCase = (value: string) =>
  value
    .trim()
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .replace(/[^a-zA-Z0-9_]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();

const normalizeParamValue = (value: TelemetryValue): TelemetryPrimitive | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "boolean") return value;
  const normalized = value.trim();
  if (!normalized) return null;
  return normalized.slice(0, 120);
};

const sanitizeParams = (params: TelemetryParams) => {
  const sanitized: Record<string, TelemetryPrimitive> = {};
  Object.entries(params).forEach(([rawKey, rawValue]) => {
    const key = asSnakeCase(rawKey);
    if (!key || BLOCKED_PARAM_KEYS.has(key)) return;
    const value = normalizeParamValue(rawValue);
    if (value === null) return;
    sanitized[key] = value;
  });
  return sanitized;
};

const isValidMeasurementId = (value: string) => /^G-[A-Z0-9]+$/i.test(value.trim());

const getConfiguredMeasurementId = () => {
  const envValue = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;
  if (!envValue) return null;
  const normalized = envValue.trim();
  return normalized || null;
};

const ensureGtag = (): GtagFn | null => {
  if (typeof window === "undefined") return null;

  window.dataLayer = window.dataLayer ?? [];
  if (!window.gtag) {
    window.gtag = function gtag() {
      // Preserve gtag's expected queue entry shape via an arguments object.
      window.dataLayer?.push(arguments);
    };
  }
  return window.gtag ?? null;
};

const ensureGaScript = (measurementId: string) => {
  if (typeof document === "undefined") return;
  if (document.getElementById(SCRIPT_ID)) return;
  const script = document.createElement("script");
  script.id = SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(script);
};

const safeCallGtag = (
  command: "js" | "config" | "event",
  target: string | Date,
  params?: Record<string, unknown>
) => {
  const gtag = ensureGtag();
  if (!gtag) return;
  try {
    if (params === undefined) {
      gtag(command, target);
      return;
    }
    gtag(command, target, params);
  } catch {
    // Telemetry should never block core app behavior.
  }
};

export const initTelemetry = (options: InitTelemetryOptions = {}) => {
  if (initialized) return enabled;
  initialized = true;

  if (typeof window === "undefined") {
    enabled = false;
    return enabled;
  }

  const measurementId = options.measurementId ?? getConfiguredMeasurementId();
  if (!measurementId || !isValidMeasurementId(measurementId)) {
    enabled = false;
    return enabled;
  }

  activeMeasurementId = measurementId;
  enabled = true;
  ensureGaScript(measurementId);
  safeCallGtag("js", new Date());
  safeCallGtag("config", measurementId, {
    send_page_view: false,
    anonymize_ip: true,
    transport_type: "beacon"
  });

  return enabled;
};

export const setTelemetryUserIdentity = (anonymousUserId: string) => {
  if (!enabled || !activeMeasurementId) return;
  const normalized = anonymousUserId.trim();
  if (!normalized) return;
  safeCallGtag("config", activeMeasurementId, {
    user_id: normalized.slice(0, 80)
  });
};

export const buildTelemetryContext = ({
  anonymousUserId,
  sessionId
}: {
  anonymousUserId: string;
  sessionId?: string | null;
}): TelemetryParams => ({
  anonymous_user_id: anonymousUserId,
  session_id: sessionId ? sessionId.toUpperCase() : undefined,
  online:
    typeof navigator !== "undefined" && typeof navigator.onLine === "boolean"
      ? navigator.onLine
      : true,
  emitted_at: new Date().toISOString()
});

export const trackTelemetry = (
  eventName: TelemetryEventName,
  params: TelemetryParams = {}
) => {
  if (!enabled) return;
  const sanitized = sanitizeParams(params);
  safeCallGtag("event", eventName, sanitized);
};

export const __resetTelemetryForTests = () => {
  activeMeasurementId = null;
  initialized = false;
  enabled = false;
};
